/**
 * KioskWelcome — public-facing welcome screen (ADR-073 Decision 5).
 *
 * "Kiosk mode is ONE without sign-in." The visitor sees a simple welcome
 * screen. Entering info (scanning ID or typing a name) transitions
 * naturally into the check-in flow.
 */
import { useState } from 'react';
import { Box, Center, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Button as ButtonComponent } from '@themes/aegix';
import { useKiosk } from '../../contexts/KioskContext';
import { KioskExitDialog } from '../../components/kiosk';
import { useNavigate } from 'react-router-dom';
import {
  HiArrowRightStartOnRectangle,
  HiIdentification,
  HiLockClosed,
  HiPencilSquare,
} from 'react-icons/hi2';
import { useCanExitKiosk } from '../../utils/permission-list';

export function KioskWelcome() {
  const { t } = useTranslation();
  usePageTitle('Kiosk');
  const { schoolName, buildingName } = useKiosk();
  const navigate = useNavigate();
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const canExitKiosk = useCanExitKiosk();

  // Long-press on the school name opens the exit dialog (hidden affordance)
  const handleLogoPointerDown = () => {
    if (!canExitKiosk) return;
    const timer = setTimeout(() => {
      setExitDialogOpen(true);
    }, 3000); // 3 second long-press
    setLongPressTimer(timer);
  };

  const handleLogoPointerUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="bg.subtle"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Center maxW="800px" w="100%" px={8}>
        <VStack gap={10} align="center" w="100%">
          {/* School branding — long-press opens exit dialog */}
          <Box
            textAlign="center"
            userSelect="none"
            onPointerDown={handleLogoPointerDown}
            onPointerUp={handleLogoPointerUp}
            onPointerLeave={handleLogoPointerUp}
            cursor="default"
          >
            <Heading as="h1" size="2xl" color="text.primary" mb={2}>
              Welcome
            </Heading>
            <Text fontSize="xl" color="text.secondary">
              {schoolName || 'Visitor Check-In'}
            </Text>
            {buildingName && (
              <Text fontSize="md" color="text.secondary" mt={1}>
                {buildingName}
              </Text>
            )}
          </Box>

          {/* Two-column: Check In | Check Out */}
          <HStack
            gap={8}
            w="100%"
            align="stretch"
            flexDirection={{ base: 'column', md: 'row' }}
          >
            {/* Check In column */}
            <VStack gap={4} flex={1}>
              <Heading as="h2" size="lg" color="text.primary">
                Check In
              </Heading>
              <ButtonComponent
                visual="primary"
                onClick={() => navigate('/kiosk/check-in?mode=scan')}
                width="100%"
                size="lg"
                data-e2e="kiosk-scan-id-button"
              >
                <HiIdentification size={24} />
                <Text ml={3} fontSize="lg">
                  Scan ID
                </Text>
              </ButtonComponent>
              <ButtonComponent
                visual="secondary"
                onClick={() => navigate('/kiosk/check-in?mode=manual')}
                width="100%"
                size="lg"
                data-e2e="kiosk-manual-entry-button"
              >
                <HiPencilSquare size={24} />
                <Text ml={3} fontSize="lg">
                  Enter Your Name
                </Text>
              </ButtonComponent>
            </VStack>

            {/* Check Out column */}
            <VStack gap={4} flex={1}>
              <Heading as="h2" size="lg" color="text.primary">
                Check Out
              </Heading>
              <ButtonComponent
                visual="primary"
                onClick={() => navigate('/kiosk/check-out')}
                width="100%"
                size="lg"
                data-e2e="kiosk-check-out-button"
              >
                <HiArrowRightStartOnRectangle size={24} />
                <Text ml={3} fontSize="lg">
                  Check Out
                </Text>
              </ButtonComponent>
            </VStack>
          </HStack>
        </VStack>
      </Center>

      {/* Subtle exit affordance for staff */}
      {canExitKiosk && (
        <Box
          as="button"
          position="fixed"
          bottom={4}
          right={4}
          cursor="pointer"
          opacity={0.3}
          _hover={{ opacity: 0.6 }}
          onClick={() => setExitDialogOpen(true)}
          p={2}
          minH="44px"
          minW="44px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          aria-label={t('kiosk.exitKioskMode', 'Exit kiosk mode')}
        >
          <HiLockClosed size={20} />
        </Box>
      )}

      <KioskExitDialog
        isOpen={exitDialogOpen}
        onClose={() => setExitDialogOpen(false)}
      />
    </Box>
  );
}
